import { useState } from 'react'
import { clsx } from 'clsx'
import { Calculator, Save } from 'lucide-react'
import { useCompetitionTeams } from '@/features/squad/queries'
import {
  usePreviewPrices,
  useApplyPrices,
  DEFAULT_TUNING,
  type PreviewPlayerPriceItem,
  type PreviewPricesResult,
  type PricingTuning,
} from '../queries'
import styles from './PricePreviewForm.module.css'

type PricePreviewFormProps = {
  competitionId: string
}

function parseSeasons(raw: string): number[] {
  return raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isInteger(n) && n >= 2000 && n <= 2030)
}

export function PricePreviewForm({ competitionId }: PricePreviewFormProps) {
  const [teamId,  setTeamId]  = useState('')
  const [seasons, setSeasons] = useState('2024, 2025, 2026')
  const [tuning,  setTuning]  = useState<PricingTuning>(DEFAULT_TUNING)

  const { data: teams, isPending: teamsPending } = useCompetitionTeams(competitionId)
  const preview = usePreviewPrices()
  const apply   = useApplyPrices()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const parsed = parseSeasons(seasons)
    if (!teamId || parsed.length === 0) return
    preview.mutate({ competitionId, teamId, seasons: parsed, tuning })
  }

  function handleSave() {
    const result = preview.data
    if (!result) return
    const prices = result.players
      .filter((p) => !p.fetchError)
      .map((p) => ({ playerId: p.playerId, price: p.suggestedPrice }))
    if (prices.length > 0) apply.mutate({ competitionId, prices })
  }

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Prévia de Preços</h3>
      <p className={styles.hint}>
        Calcula o preço sugerido dos jogadores de uma seleção a partir das estatísticas das
        últimas temporadas. Revise, ajuste os multiplicadores e <strong>salve</strong> os preços
        desta seleção no banco.
      </p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Seleção</span>
            <select
              className={styles.input}
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              disabled={teamsPending || preview.isPending}
            >
              <option value="">Selecione um time…</option>
              {teams?.map((t) => (
                <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Temporadas (mescladas)</span>
            <input
              type="text"
              className={styles.input}
              value={seasons}
              placeholder="2024, 2025, 2026"
              onChange={(e) => setSeasons(e.target.value)}
              disabled={preview.isPending}
            />
          </label>
        </div>

        <TuningControls tuning={tuning} onChange={setTuning} disabled={preview.isPending} />

        <button type="submit" className={styles.btn} disabled={!teamId || preview.isPending}>
          {preview.isPending ? <span className={styles.spinner} aria-hidden="true" /> : <Calculator size={14} />}
          {preview.isPending ? 'Calculando…' : 'Gerar Prévia'}
        </button>
      </form>

      {preview.data && (
        <>
          <PreviewTable result={preview.data} />
          <SaveBar result={preview.data} onSave={handleSave} saving={apply.isPending} />
        </>
      )}
    </div>
  )
}

function TuningControls({ tuning, onChange, disabled }: {
  tuning:   PricingTuning
  onChange: (t: PricingTuning) => void
  disabled: boolean
}) {
  function set(key: keyof PricingTuning, value: number) {
    onChange({ ...tuning, [key]: value })
  }
  return (
    <div className={styles.tuning}>
      <span className={styles.tuningTitle}>Ajustes (multiplicadores)</span>
      <div className={styles.tuningGrid}>
        <TuneInput label="Escala" value={tuning.priceScale}    step={0.001} onChange={(v) => set('priceScale', v)}    disabled={disabled} />
        <TuneInput label="× ATA"  value={tuning.fwdMultiplier} step={0.05}  onChange={(v) => set('fwdMultiplier', v)} disabled={disabled} />
        <TuneInput label="× MEI"  value={tuning.midMultiplier} step={0.05}  onChange={(v) => set('midMultiplier', v)} disabled={disabled} />
        <TuneInput label="× DEF"  value={tuning.defMultiplier} step={0.05}  onChange={(v) => set('defMultiplier', v)} disabled={disabled} />
        <TuneInput label="× GOL"  value={tuning.gkMultiplier}  step={0.05}  onChange={(v) => set('gkMultiplier', v)}  disabled={disabled} />
      </div>
    </div>
  )
}

function TuneInput({ label, value, step, onChange, disabled }: {
  label:    string
  value:    number
  step:     number
  onChange: (v: number) => void
  disabled: boolean
}) {
  return (
    <label className={styles.tuneField}>
      <span className={styles.tuneLabel}>{label}</span>
      <input
        type="number"
        className={styles.tuneInput}
        value={value}
        step={step}
        min={0}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
      />
    </label>
  )
}

function SaveBar({ result, onSave, saving }: {
  result: PreviewPricesResult
  onSave: () => void
  saving: boolean
}) {
  const saveable = result.players.filter((p) => !p.fetchError).length
  return (
    <div className={styles.saveBar}>
      <span className={styles.saveNote}>
        Grava o preço sugerido de {saveable} jogador(es) de {result.teamCode} no banco.
      </span>
      <button
        type="button"
        className={styles.saveBtn}
        onClick={onSave}
        disabled={saving || saveable === 0}
      >
        {saving ? <span className={styles.spinner} aria-hidden="true" /> : <Save size={14} />}
        {saving ? 'Salvando…' : 'Salvar preços'}
      </button>
    </div>
  )
}

function PreviewTable({ result }: { result: NonNullable<ReturnType<typeof usePreviewPrices>['data']> }) {
  return (
    <div className={styles.result}>
      <div className={styles.resultHeader}>
        <span className={styles.resultTitle}>
          {result.teamCode} — {result.playersEvaluated} jogadores
        </span>
        {result.lowDataCount > 0 && (
          <span className={styles.lowDataNote}>{result.lowDataCount} sem dados (no piso)</span>
        )}
      </div>

      {result.warnings.length > 0 && (
        <div className={styles.warnings}>
          {result.warnings.map((w, i) => <p key={i}>{w}</p>)}
        </div>
      )}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thLeft}>Jogador</th>
              <th>Pos</th>
              <th>Atual</th>
              <th>Sugerido</th>
              <th>Rating</th>
              <th>Min C/S</th>
              <th>G/A clube</th>
              <th>G/A sel</th>
              <th>Def</th>
              <th>Liga</th>
              <th>EV</th>
            </tr>
          </thead>
          <tbody>
            {result.players.map((p) => <PreviewRow key={p.playerId} p={p} />)}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PreviewRow({ p }: { p: PreviewPlayerPriceItem }) {
  return (
    <tr className={clsx(p.fetchError ? styles.fetchErrorRow : p.lowData && styles.lowDataRow)}>
      <td className={styles.tdLeft}>{p.displayName}</td>
      <td>{p.position || '—'}</td>
      <td className={styles.num}>{p.currentPrice.toFixed(1)}</td>
      <td className={clsx(styles.num, styles.suggested)}>
        {p.fetchError ? '— erro' : p.suggestedPrice.toFixed(1)}
      </td>
      <td className={styles.num}>{p.clubRating.toFixed(2)}</td>
      <td className={styles.num}>{Math.round(p.clubMinutes)}/{Math.round(p.ntMinutes)}</td>
      <td className={styles.num}>{Math.round(p.goals)}/{Math.round(p.assists)}</td>
      <td className={styles.num}>{Math.round(p.ntGoals)}/{Math.round(p.ntAssists)}</td>
      <td className={styles.num}>{Math.round(p.defActions)}</td>
      <td className={styles.num}>{p.leagueStrength.toFixed(2)}</td>
      <td className={styles.num}>{p.ev.toFixed(0)}</td>
    </tr>
  )
}
